import { useState, useEffect } from 'react';
import { X, ArrowRight, Clock } from 'lucide-react';
import { createContact } from '@/lib/api';
import { toast } from 'sonner';

export default function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    // Check if popup was already shown
    const popupShown = sessionStorage.getItem('wizbang_exit_popup_shown');
    if (popupShown) return;

    let timeout;
    
    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of page
      if (e.clientY <= 0) {
        setShow(true);
        sessionStorage.setItem('wizbang_exit_popup_shown', 'true');
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
    };

    // Add delay before enabling exit intent
    timeout = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);

    // Also show after 30 seconds if user hasn't converted
    const scrollTimeout = setTimeout(() => {
      const popupShownNow = sessionStorage.getItem('wizbang_exit_popup_shown');
      if (!popupShownNow) {
        setShow(true);
        sessionStorage.setItem('wizbang_exit_popup_shown', 'true');
      }
    }, 30000);

    return () => {
      clearTimeout(timeout);
      clearTimeout(scrollTimeout);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await createContact({
        name: 'Exit Intent Lead',
        email: email,
        phone: '',
        subject: '[EXIT INTENT] Quick Lead Capture',
        message: 'Lead captured via exit intent popup',
      });
      setSubmitted(true);
      toast.success('We\'ll send you the guide shortly!');
      setTimeout(() => setShow(false), 3000);
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      data-testid="exit-intent-popup"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={() => setShow(false)}
      />

      {/* Popup */}
      <div className="relative bg-[#0A0A0A] border border-[#1F1F1F] max-w-lg w-full p-8 animate-scaleIn">
        {/* Close button */}
        <button
          onClick={() => setShow(false)}
          className="absolute top-4 right-4 text-[#555] hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-syne text-2xl font-bold text-white mb-2">Check Your Email!</h3>
            <p className="text-[#888]">We're sending the free guide to your inbox.</p>
          </div>
        ) : (
          <>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#ff5a1f]/10 border border-[#ff5a1f]/20 px-3 py-1 mb-6">
              <Clock className="w-4 h-4 text-[#ff5a1f]" />
              <span className="text-[#ff5a1f] text-xs font-bold uppercase tracking-wider">Wait! Don't Go</span>
            </div>

            <h2 className="font-syne text-2xl md:text-3xl font-bold text-white mb-4">
              Get Our Free
              <br />
              <span className="text-[#ff5a1f]">Growth Playbook</span>
            </h2>

            <p className="text-[#888] mb-6">
              Download our exclusive guide: <strong className="text-white">"7 Revenue Systems Every Brand Needs"</strong> — absolutely free.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-4 text-sm outline-none transition-colors placeholder:text-[#555]"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#ff5a1f] text-white px-6 py-4 text-sm font-bold uppercase tracking-wider hover:bg-[#e04e1a] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Me The Free Guide'}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <p className="text-[#555] text-xs text-center mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
